import {addToast} from '@heroui/toast';
import {Icon} from '@iconify/react';
import {useQuery} from '@tanstack/react-query';
import {motion} from 'framer-motion';
import {ReactNode, useEffect, useMemo, useState} from 'react';

import services from '../services';
import {ChampionReveal, Confetti, RouteList, SideAvatars} from '../tournaments/live/RoadToVictory';
import {cardFileName, markShareSeen, prefersReducedMotion, resultsHeading, ResultsPayload, roadHeading, sequence, ShareChannel, shareImage} from './resultsModel';

/** A beat of the sequence: arrives after `delay` unless motion is reduced, when it is simply there. */
function Beat({delay, reduced, children, className}: {delay: number; reduced: boolean; children: ReactNode; className?: string}) {
    if (reduced) return <div className={className}>{children}</div>;
    return (
        <motion.div className={className} initial={{opacity: 0, y: 14}} animate={{opacity: 1, y: 0}} transition={{delay, duration: 0.35}}>
            {children}
        </motion.div>
    );
}

const MEDAL = {2: {icon: 'mdi:medal', color: 'text-[#c0c7d4]', word: 'Runner-up'}, 3: {icon: 'mdi:medal-outline', color: 'text-[#d9a066]', word: 'Third'}} as const;

/** 2nd and 3rd beneath the champion, sliding in; a league's places carry their points. */
export function Podium({podium, reduced, delay}: {podium: ResultsPayload['podium']; reduced: boolean; delay: number}) {
    const rest = podium.filter((p) => p.place === 2 || p.place === 3);
    if (!rest.length) return null;
    return (
        <ol className="mt-5 grid w-full gap-2 sm:grid-cols-2" aria-label="Podium">
            {rest.map((p, i) => {
                const medal = MEDAL[p.place as 2 | 3];
                const row = (
                    <li key={p.place} className="flex items-center gap-3 rounded-xl bg-white/[0.08] px-3 py-2.5 ring-1 ring-white/10">
                        <Icon icon={medal.icon} className={'shrink-0 text-2xl ' + medal.color} />
                        <SideAvatars label={p.label} photos={p.photos} size={36} />
                        <span className="min-w-0 flex-1">
                            <span className="block text-[11px] font-bold uppercase tracking-wider text-white/60">{p.place}. {medal.word}</span>
                            <span className="block truncate text-sm font-semibold text-white">{p.label}</span>
                        </span>
                        {p.metric && <span className="shrink-0 text-xs font-bold tabular-nums text-[#f5c518]">{p.metric}</span>}
                    </li>
                );
                return reduced ? row : (
                    <motion.div key={p.place} initial={{opacity: 0, y: 18}} animate={{opacity: 1, y: 0}} transition={{delay: delay + i * 0.15, duration: 0.3}}>{row}</motion.div>
                );
            })}
        </ol>
    );
}

/**
 * The completion screen, as a moment rather than a form: the champion's road to victory as the
 * centrepiece, the podium beneath it, the results card with Share image under it, and the share
 * buttons arriving last so the eye lands on them. Under two seconds, buttons never blocked, the
 * final state straight away under reduced motion. Mobile first: it is mostly seen on a phone.
 */
export function ShareResultsView({payload, onClose, reduced: reducedProp, onShared}: {
    payload: ResultsPayload;
    onClose: () => void;
    reduced?: boolean;
    onShared?: (channel: ShareChannel) => void;
}) {
    const reduced = reducedProp ?? prefersReducedMotion();
    const t = sequence(reduced);
    const [cardReady, setCardReady] = useState(false);
    const [cardFailed, setCardFailed] = useState(false);
    const [busy, setBusy] = useState<ShareChannel | null>(null);
    const {event, champion, podium, route, kind} = payload;

    const shared = (channel: ShareChannel) => {
        onShared?.(channel);
    };
    const copyMessage = async () => {
        try {
            await navigator.clipboard.writeText(payload.message);
            addToast({color: 'success', title: 'Message copied'});
            shared('copy');
        } catch {
            addToast({color: 'warning', title: 'Could not copy — long-press the message to copy it.'});
        }
    };
    const share = async (size: 'feed' | 'story') => {
        const channel: ShareChannel = size === 'story' ? 'story' : 'image';
        setBusy(channel);
        try {
            const how = await shareImage(payload.card[size], cardFileName(payload, size), `${event.title} — results`);
            if (how === 'downloaded') addToast({color: 'success', title: 'Card saved — share it anywhere.'});
            shared(channel);
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || 'The card could not be shared.'});
        } finally {
            setBusy(null);
        }
    };

    const subtitle = [champion.decided_by === 'final' ? 'Won the final' : 'Topped the ' + (kind === 'tournament' ? 'standings' : 'table'), event.sport_label].filter(Boolean).join(' · ');

    return (
        <div role="dialog" aria-modal="true" aria-label="Share the results" className="fixed inset-0 z-[110] overflow-y-auto bg-[#04102e]/[0.97] backdrop-blur-sm">
            {!reduced && <Confetti once count={90} />}
            <button type="button" onClick={onClose} aria-label="Close"
                    className="fixed right-4 top-4 z-10 rounded-full bg-white/10 p-2 text-white/70 transition hover:bg-white/20 hover:text-white">
                <Icon icon="mdi:close" className="text-xl" />
            </button>

            <div className="relative mx-auto flex w-full max-w-lg flex-col items-center px-4 pb-10 pt-14 sm:pt-16">
                <p className="mb-2 max-w-full truncate text-[11px] font-bold uppercase tracking-[0.3em] text-[#cddbff]">{event.title}</p>

                {/* The centrepiece: the champion, then their road. */}
                <ChampionReveal winner={champion} subtitle={subtitle} reduced={reduced} size="md" />
                {route.length > 0 && (
                    <Beat delay={t.road + 0.5} reduced={reduced} className="mt-5 w-full">
                        <p className="mb-2 text-center text-[11px] font-bold uppercase tracking-[0.3em] text-[#cddbff]">{roadHeading(kind)}</p>
                        <RouteList route={route} reduced={reduced} delay={t.road + 0.55} stagger={t.legStagger} />
                    </Beat>
                )}

                <Podium podium={podium} reduced={reduced} delay={t.podium} />

                {/* The card, with Share image under it. */}
                <Beat delay={t.card} reduced={reduced} className="mt-7 flex w-full flex-col items-center">
                    <div className="relative w-full max-w-[300px] -rotate-2 overflow-hidden rounded-2xl bg-[#06133e] shadow-[0_24px_60px_rgba(0,0,0,0.55)] ring-1 ring-white/15" style={{aspectRatio: '1 / 1'}}>
                        {!cardFailed && (
                            <img src={payload.card.feed} alt={`${event.title} results card`} className={'h-full w-full object-cover transition-opacity ' + (cardReady ? 'opacity-100' : 'opacity-0')}
                                 onLoad={() => setCardReady(true)} onError={() => setCardFailed(true)} />
                        )}
                        {!cardReady && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/70">
                                <Icon icon={cardFailed ? 'mdi:image-off-outline' : 'mdi:loading'} className={'text-3xl ' + (cardFailed ? '' : 'animate-spin')} />
                                <span className="text-xs font-semibold">{cardFailed ? 'The card is not ready yet' : 'Preparing your card…'}</span>
                            </div>
                        )}
                    </div>
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                        <button type="button" onClick={() => share('feed')} disabled={busy !== null}
                                className="inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-2.5 text-sm font-semibold text-white ring-1 ring-white/20 transition hover:bg-white/20 disabled:opacity-60">
                            <Icon icon={busy === 'image' ? 'mdi:loading' : 'mdi:image-outline'} className={'text-lg ' + (busy === 'image' ? 'animate-spin' : '')} /> Share image
                        </button>
                        <button type="button" onClick={() => share('story')} disabled={busy !== null}
                                className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white/80 transition hover:text-white disabled:opacity-60">
                            <Icon icon={busy === 'story' ? 'mdi:loading' : 'mdi:cellphone'} className={'text-lg ' + (busy === 'story' ? 'animate-spin' : '')} /> Share to story
                        </button>
                    </div>
                </Beat>

                {/* The share buttons last, so the eye lands on them. Never blocked: only their opacity waits. */}
                <Beat delay={t.buttons} reduced={reduced} className="mt-8 w-full">
                    <div className="rounded-2xl bg-white/[0.06] p-4 ring-1 ring-white/10">
                        <p className="whitespace-pre-line text-sm leading-relaxed text-white/85">{payload.message}</p>
                        <div className="mt-4 grid grid-cols-2 gap-2">
                            <a href={payload.whatsapp_url} target="_blank" rel="noopener noreferrer" onClick={() => shared('whatsapp')}
                               className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-sm font-bold text-[#04102e] transition hover:brightness-110">
                                <Icon icon="mdi:whatsapp" className="text-xl" /> Send on WhatsApp
                            </a>
                            <button type="button" onClick={copyMessage}
                                    className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 px-4 py-3 text-sm font-bold text-white ring-1 ring-white/20 transition hover:bg-white/20">
                                <Icon icon="mdi:content-copy" className="text-lg" /> Copy
                            </button>
                        </div>
                        <p className="mt-3 truncate text-center text-xs text-white/60">
                            <a href={payload.public_url} target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:underline">{payload.public_url.replace(/^https?:\/\//, '')}</a>
                        </p>
                    </div>
                    <p className="mt-4 text-center text-xs text-white/60">{payload.claim_line}</p>
                    <div className="mt-5 flex justify-center">
                        <button type="button" onClick={onClose} className="rounded-full bg-white/10 px-6 py-2.5 text-sm font-semibold text-white ring-1 ring-white/20 transition hover:bg-white/20">Done</button>
                    </div>
                </Beat>
            </div>
        </div>
    );
}

/**
 * "Share the results" for one finished event: reads the share payload and shows the screen. Mount it
 * with `open` after Finish, or from the finished event's Home card; it closes itself through onClose.
 */
export default function ShareResultsScreen({eventUid, open, onClose}: {eventUid: string; open: boolean; onClose: () => void}) {
    const query = useQuery({
        queryKey: ['event-results', eventUid],
        queryFn: () => services.results.get(eventUid),
        enabled: open && !!eventUid,
        staleTime: 60_000,
        retry: 1,
    });
    const reduced = useMemo(prefersReducedMotion, []);

    // Escape closes; and however it was opened, this browser has now seen this event's screen.
    useEffect(() => {
        if (!open) return;
        markShareSeen(eventUid);
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;
    if (query.isLoading) {
        return (
            <div role="dialog" aria-modal="true" aria-label="Share the results" className="fixed inset-0 z-[110] flex items-center justify-center bg-[#04102e]/[0.97]">
                <div className="flex flex-col items-center gap-3 text-white/80"><Icon icon="mdi:loading" className="animate-spin text-3xl" /><span className="text-sm">Gathering the results…</span></div>
            </div>
        );
    }
    if (!query.data) {
        return (
            <div role="dialog" aria-modal="true" aria-label="Share the results" className="fixed inset-0 z-[110] flex items-center justify-center bg-[#04102e]/[0.97] px-6">
                <div className="flex max-w-sm flex-col items-center gap-3 text-center text-white">
                    <Icon icon="mdi:trophy-outline" className="text-4xl text-[#f5c518]" />
                    <p className="text-sm text-white/80">The results are not ready to share yet — once the final scores are in, they will be here.</p>
                    <button type="button" onClick={onClose} className="mt-2 rounded-full bg-white/10 px-6 py-2.5 text-sm font-semibold text-white ring-1 ring-white/20">Close</button>
                </div>
            </div>
        );
    }
    return <ShareResultsView payload={query.data} onClose={onClose} reduced={reduced} onShared={(channel) => services.results.shared(eventUid, channel).catch(() => undefined)} />;
}
